import { API, PolicyWorksheetViewDTO, WorksheetItemViewDTO } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import { NUMBER_VALUE_MAX_LENGTH, NUMBER_VALUE_PATTERN, TEXT_VALUE_MAX_LENGTH } from "@swivl-mono/policy-definitions";
import { PolicyFieldCandidate } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonVariant, Checkbox, Input, Spinner, Switch, SwitchVariant, Tooltip } from "@swivl-mono/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiCheck, FiRotateCcw, FiX } from "react-icons/fi";

import { Member } from "../../../Models/Member/Member.Current";
import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";
import { PolicyRadio } from "../../Properties/PolicyWorksheets/PolicyRadio";
import { Badge } from "./Badge";

// The field-level row + control (CandidateRow) used below. Local to this component — the shared,
// deny-less PolicyFieldReviewView (doc-ingest / the Cortex migrator) doesn't need a deny toggle,
// but this component's approve/deny/defer flow does.

// Below this the extraction is shaky enough to flag; above it we stay quiet.
const LOW_CONFIDENCE = 0.7;

interface CandidateRowProps {
  candidate: PolicyFieldCandidate;
  item?: WorksheetItemViewDTO;
  saving: boolean;
  onSave: (value: string) => Promise<void>;
  /** When the operator has denied this suggestion (excluded from apply). */
  denied?: boolean;
  /** Toggles deny; the ✕/undo control only renders when provided. */
  onToggleDeny?: () => void;
}

function CandidateRow({ candidate, item, saving, onSave, denied, onToggleDeny }: CandidateRowProps): React.ReactElement {
  // The free input reports its live draft state so the badge/reason follow what's typed,
  // not the last-committed value (which lags behind an in-progress edit).
  const [live, setLive] = useState<FreeInputLiveState>({ invalid: false, dirty: false });
  return (
    <div className={filterClasses("py-4", (candidate.diff === "matches" || denied) && "opacity-60")}>
      <div className="mb-2 flex items-start justify-between gap-4">
        <label className="body-sm-alt text-grey-900">{item?.label ?? candidate.mapKey}</label>
        <span className="flex shrink-0 items-center gap-2">
          {denied ? <Badge className="bg-grey-100 text-grey-600">Denied</Badge> : <DiffBadge candidate={candidate} item={item} live={live} />}
          {/* Only surface confidence when it's low enough to warrant a second look. */}
          {candidate.confidence < LOW_CONFIDENCE && (
            <span className="text-xs text-grey-500">{Math.round(candidate.confidence * 100)}% confident</span>
          )}
          {onToggleDeny && (
            <Tooltip content={denied ? "Restore suggestion" : "Deny suggestion"}>
              <button type="button" className="flex text-grey-400 hover:text-grey-700" onClick={onToggleDeny}>
                {denied ? <FiRotateCcw className="size-4" /> : <FiX className="size-4" />}
              </button>
            </Tooltip>
          )}
        </span>
      </div>

      {item ? (
        <ProposedControl
          mapKey={candidate.mapKey}
          item={item}
          value={candidate.proposedValue ?? ""}
          disabled={saving || !!denied}
          onCommit={onSave}
          onLiveStateChange={setLive}
        />
      ) : (
        <p className="body-sm text-grey-500">This field isn’t in the current definitions ({candidate.mapKey}).</p>
      )}

      {/* Stale server reason only when not mid-edit; a live invalid draft shows its own error below the input. */}
      {candidate.status === "needs-review" && candidate.reason && !live.dirty && !live.invalid && (
        <p className="body-sm mt-2 text-red-600">Won’t apply: {candidate.reason}</p>
      )}

      {candidate.source.snippet && (
        <p className="body-sm mt-2 border-l-2 border-grey-200 pl-2.5 text-grey-500 italic">“{candidate.source.snippet}”</p>
      )}
    </div>
  );
}

/** A free input's live state: `invalid` = current draft fails validation; `dirty` = differs from the committed value. */
interface FreeInputLiveState {
  invalid: boolean;
  dirty: boolean;
}

interface ProposedControlProps {
  mapKey: string;
  item: WorksheetItemViewDTO;
  value: string;
  disabled: boolean;
  onCommit: (value: string) => Promise<void>;
  /** Free inputs report their live draft state; other control kinds are always valid and clean. */
  onLiveStateChange?: (state: FreeInputLiveState) => void;
}

/** Renders the field's Policy-Builder control bound to the proposed value. */
function ProposedControl({ mapKey, item, value, disabled, onCommit, onLiveStateChange }: ProposedControlProps): React.ReactElement {
  if (item.kind === "boolean_checkbox" || item.kind === "additive_checkbox") {
    return (
      <Switch
        variant={SwitchVariant.TOGGLE}
        activeColor="orange"
        on={value === "1"}
        isDisabled={disabled}
        onSet={(next) => {
          void onCommit(next ? "1" : "0");
        }}
      />
    );
  }

  if (item.kind === "radio" || item.kind === "dropdown" || item.kind === "parameterized_option") {
    return (
      <PolicyRadio
        value={value}
        disabled={disabled}
        options={(item.options ?? []).map((option) => ({ label: option.label, value: option.optionKey }))}
        onChange={(next) => {
          void onCommit(next);
        }}
      />
    );
  }

  if (item.kind === "multi_checkbox") {
    const selected = new Set(value.split(",").filter((token) => token !== ""));
    return (
      <div className="flex flex-col gap-2" role="group">
        {(item.options ?? []).map((option) => (
          <Checkbox
            key={option.optionKey}
            name={`${mapKey}-${option.optionKey}`}
            label={option.label}
            disabled={disabled}
            checked={selected.has(option.optionKey)}
            onCheckedChange={(checked) => {
              const next = new Set(selected);
              if (checked === true) next.add(option.optionKey);
              else next.delete(option.optionKey);
              // Preserve definition order in the comma-joined value.
              const ordered = (item.options ?? []).map((o) => o.optionKey).filter((key) => next.has(key));
              void onCommit(ordered.join(","));
            }}
          />
        ))}
      </div>
    );
  }

  // text_value / number_value — commit on blur so we persist once, not per keystroke.
  return <FreeInput mapKey={mapKey} item={item} value={value} disabled={disabled} onCommit={onCommit} onLiveStateChange={onLiveStateChange} />;
}

// Live client-side check mirroring the server's validateAnswerValue, so the error
// shows as the operator types instead of only after a doomed commit.
function freeInputError(raw: string, isNumber: boolean): string | null {
  const value = raw.trim();
  if (value.length === 0) return null;
  if (isNumber) {
    if (value.length > NUMBER_VALUE_MAX_LENGTH) return `Must be at most ${NUMBER_VALUE_MAX_LENGTH} characters.`;
    if (!NUMBER_VALUE_PATTERN.test(value.replace(/[$,%\s]/g, ""))) return "Must be a plain non-negative number.";
    return null;
  }
  if (/[\r\n]/.test(value)) return "Must be a single line.";
  if (value.length > TEXT_VALUE_MAX_LENGTH) return `Must be at most ${TEXT_VALUE_MAX_LENGTH} characters.`;
  if (value.includes("{{") || value.includes("}}")) return 'Must not contain "{{" or "}}".';
  return null;
}

/** Text/number input with a local draft, live validation, committed on blur when valid. */
function FreeInput({ item, value, disabled, onCommit, onLiveStateChange }: ProposedControlProps): React.ReactElement {
  const [draft, setDraft] = useState(value);
  const lastValue = useRef(value);

  // Pick up server-side value changes without clobbering an in-progress edit.
  useEffect(() => {
    if (value !== lastValue.current) {
      lastValue.current = value;
      setDraft(value);
    }
  }, [value]);

  const isNumber = item.kind === "number_value";
  const error = freeInputError(draft, isNumber);
  const dirty = draft.trim() !== value;

  // Report live state so the badge/reason follow the draft, not the last-committed value.
  useEffect(() => {
    onLiveStateChange?.({ invalid: error !== null, dirty });
  }, [error, dirty, onLiveStateChange]);
  return (
    <>
      <Input
        name={item.itemKey}
        value={draft}
        disabled={disabled}
        type={isNumber ? "number" : undefined}
        min={isNumber ? 0 : undefined}
        inputMode={isNumber ? "decimal" : undefined}
        placeholder={item.placeholder ? "e.g. " + item.placeholder : undefined}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          // Commit on blur; an invalid value persists as needs-review — kept on screen, excluded from apply.
          if (draft.trim() !== value) void onCommit(draft.trim());
        }}
      />
      {error && <p className="body-sm mt-1 text-red-600">{error}</p>}
    </>
  );
}

interface DiffBadgeProps {
  candidate: PolicyFieldCandidate;
  item?: WorksheetItemViewDTO;
  /** The free input's live draft state, so the badge follows what's typed, not the last commit. */
  live?: FreeInputLiveState;
}

// While editing, the live draft wins: invalid -> "Won't apply", a valid edit -> "Will change".
// Otherwise fall back to the committed candidate's diff.
function badgeFor(candidate: PolicyFieldCandidate, isToggle: boolean, live?: FreeInputLiveState): { text: string; className: string } {
  if (live?.invalid) return { text: "Won’t apply", className: "bg-red-50 text-red-700" };
  if (live?.dirty) return { text: "Will change", className: "bg-orange-100 text-orange-700" };
  return diffBadgeContent(candidate, isToggle);
}

function DiffBadge({ candidate, item, live }: DiffBadgeProps): React.ReactElement {
  const isToggle = item ? item.kind === "boolean_checkbox" || item.kind === "additive_checkbox" : false;
  const { text, className } = badgeFor(candidate, isToggle, live);
  const current = currentValueLabel(candidate, item);
  if (current == null) return <Badge className={className}>{text}</Badge>;
  return (
    <Tooltip content={`Currently: ${current}`}>
      <Badge className={className}>{text}</Badge>
    </Tooltip>
  );
}

// The field's current value in human form, for the diff-badge hover.
function currentValueLabel(candidate: PolicyFieldCandidate, item?: WorksheetItemViewDTO): string | null {
  const current = candidate.currentValue;
  if (current == null || current === "") return null;
  if (!item) return current;
  if (item.kind === "boolean_checkbox" || item.kind === "additive_checkbox") return current === "1" ? "on" : "off";
  if (item.kind === "multi_checkbox") {
    const labels = current
      .split(",")
      .filter(Boolean)
      .map((key) => item.options?.find((option) => option.optionKey === key)?.label ?? key);
    return labels.length > 0 ? labels.join(", ") : null;
  }
  if (item.kind === "radio" || item.kind === "dropdown" || item.kind === "parameterized_option") {
    return item.options?.find((option) => option.optionKey === current)?.label ?? current;
  }
  return current;
}

function diffBadgeContent(candidate: PolicyFieldCandidate, isToggle: boolean): { text: string; className: string } {
  if (candidate.status === "needs-review") return { text: "Won’t apply", className: "bg-red-50 text-red-700" };
  if (candidate.diff === "matches") return { text: "Won’t change", className: "bg-grey-100 text-grey-600" };
  if (candidate.diff === "pending-defs") return { text: "New field", className: "bg-grey-100 text-grey-600" };
  if (isToggle) return { text: candidate.proposedValue === "1" ? "Will turn on" : "Will turn off", className: "bg-orange-100 text-orange-700" };
  return { text: "Will change", className: "bg-orange-100 text-orange-700" };
}

interface PolicyFindingsReviewProps {
  /** The ingested_source row (scrape or upload) whose findings we're reviewing. */
  sourceId: number;
  /** Single location to apply to (scrape); omitted applies at the source's own scopes (upload). */
  locationId?: number;
  onFinish: () => void;
  /** Primary finish button label. */
  finishLabel?: string;
  /** Which layout to open in. */
  defaultView?: "step" | "all";
  /** Optional: step backward out of the review (e.g. a wizard's previous step). */
  onBack?: () => void;
  /** "Save & close" target when not all policies are reviewed yet — falls back to onFinish. */
  onClose?: () => void;
}

interface ReviewSection {
  worksheet: PolicyWorksheetViewDTO;
  candidates: PolicyFieldCandidate[];
}

/** Review a source's policy findings, step-by-step or all at once, applying on approve. */
export function PolicyFindingsReview({
  sourceId,
  locationId,
  onFinish,
  finishLabel,
  defaultView,
  onBack,
  onClose
}: PolicyFindingsReviewProps): React.ReactElement {
  const orgId = Member.orgId;
  const finishText = finishLabel ?? "Finish & go to location";
  const [candidates, setCandidates] = useState<PolicyFieldCandidate[]>([]);
  const [worksheets, setWorksheets] = useState<PolicyWorksheetViewDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [showAll, setShowAll] = useState(defaultView === "all");
  const [appliedCount, setAppliedCount] = useState(0);
  // Policy keys the reviewer pushed to the back of the line via "Save for later".
  const [deferred, setDeferred] = useState<string[]>([]);
  // Policy keys already approved; shown greyed/collapsed but still editable.
  const [approved, setApproved] = useState<Set<string>>(new Set());
  // Approved policies the operator re-opened to edit in the "all" view.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Field mapKeys the operator denied; excluded from apply.
  const [denied, setDenied] = useState<Set<string>>(new Set());
  // Findings that mattered on first load; frozen so a no-op edit doesn't vanish its step.
  const [reviewKeys, setReviewKeys] = useState<Set<string> | null>(null);
  // Set once everything's approved but the operator chose to keep editing.
  const [reviewingAgain, setReviewingAgain] = useState(false);
  // Pending approve when the selection has "Won't apply" fields; confirm before skipping them.
  const [confirming, setConfirming] = useState<{ mapKeys: string[]; finish: boolean; nextIndex: number | null; markKeys?: string[] } | null>(null);

  const load = useCallback(async () => {
    setLoading(true); // explicit, not just the initial default — so "Try again" shows the spinner too
    try {
      const [candidateList, response] = await Promise.all([
        API.DocumentIngest.getPolicyCandidates(orgId, sourceId, undefined, locationId),
        API.PolicyWorksheets.getWorksheets(orgId)
      ]);
      setLoadError(false); // clears a previous failure once a retry actually succeeds
      setCandidates(candidateList);
      setWorksheets(response.worksheets);
      setReviewKeys((prev) => prev ?? new Set(candidateList.filter((c) => c.diff !== "matches").map((c) => c.mapKey)));
    } catch (error) {
      // Without this, a failed load never clears `loading` — the spinner spins forever — and
      // the empty candidate list would otherwise fall through to "everything already matches",
      // which is not what happened.
      setLoadError(true);
      AlertsModel.errorToast(error instanceof Error ? error.message : "Couldn’t load these policy fields, please try again.");
    } finally {
      setLoading(false);
    }
  }, [orgId, sourceId, locationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = useCallback(
    async (mapKey: string, proposedValue: string) => {
      setSavingKey(mapKey);
      try {
        await API.DocumentIngest.updatePolicyFinding(orgId, sourceId, { mapKey, proposedValue, locationId });
        setCandidates(await API.DocumentIngest.getPolicyCandidates(orgId, sourceId, undefined, locationId));
      } catch (error) {
        // Without this, a failed edit fails completely silently — the input just sits there with
        // no indication the operator's change was never actually saved.
        AlertsModel.errorToast(error instanceof Error ? error.message : "Couldn’t save that edit, please try again.");
      } finally {
        setSavingKey(null);
      }
    },
    [orgId, sourceId, locationId]
  );

  const { sections, itemByMapKey } = useMemo(() => {
    const itemByMapKey = new Map<string, WorksheetItemViewDTO>();
    const worksheetByPolicyKey = new Map<string, PolicyWorksheetViewDTO>();
    for (const worksheet of worksheets) {
      worksheetByPolicyKey.set(worksheet.policyKey, worksheet);
      for (const item of worksheet.items) itemByMapKey.set(`${worksheet.policyKey}.${item.itemKey}`, item);
    }
    // Keep the frozen review set so a value edited to match current stays visible ("Won't change").
    const byPolicyKey = new Map<string, PolicyFieldCandidate[]>();
    for (const candidate of candidates.filter((c) => (reviewKeys ? reviewKeys.has(c.mapKey) : c.diff !== "matches"))) {
      const policyKey = candidate.mapKey.split(".")[0];
      const list = byPolicyKey.get(policyKey) ?? [];
      list.push(candidate);
      byPolicyKey.set(policyKey, list);
    }
    const sections: ReviewSection[] = [...byPolicyKey.entries()]
      .map(([policyKey, list]) => ({ worksheet: worksheetByPolicyKey.get(policyKey), candidates: list }))
      .filter((section): section is ReviewSection => section.worksheet !== undefined)
      .sort(
        (a, b) =>
          (a.worksheet.groupLabel ?? a.worksheet.title).localeCompare(b.worksheet.groupLabel ?? b.worksheet.title) ||
          a.worksheet.sortOrder - b.worksheet.sortOrder
      );
    return { sections, itemByMapKey };
  }, [candidates, worksheets, reviewKeys]);

  // Deferred worksheets sink to the end, preserving deferral order.
  const orderedSections = useMemo(() => {
    if (deferred.length === 0) return sections;
    const deferredSet = new Set(deferred);
    const kept = sections.filter((section) => !deferredSet.has(section.worksheet.policyKey));
    const moved = deferred
      .map((key) => sections.find((section) => section.worksheet.policyKey === key))
      .filter((section): section is ReviewSection => section !== undefined);
    return [...kept, ...moved];
  }, [sections, deferred]);

  // Apply findings to the source's scope; mark policies reviewed and advance (or finish).
  const runApprove = useCallback(
    async (mapKeys: string[], finish: boolean, nextIndex: number | null, markKeys?: string[]) => {
      setApplying(true);
      try {
        const result = await API.DocumentIngest.applyFindings(orgId, sourceId, { mapKeys, locationId, finish });
        if (result?.failed) {
          AlertsModel.errorToast(`${result.failed} policy field(s) couldn't be applied - fix them in Policy Builder directly.`);
        }
        if (finish) {
          onFinish();
          return;
        }
        setAppliedCount((count) => count + (result?.applied ?? 0));
        // Reflect the writes (applied findings now match current) before continuing.
        setCandidates(await API.DocumentIngest.getPolicyCandidates(orgId, sourceId, undefined, locationId));
        if (markKeys?.length) {
          setApproved((prev) => new Set([...prev, ...markKeys]));
          setExpanded((prev) => (prev.size ? new Set([...prev].filter((key) => !markKeys.includes(key))) : prev));
        }
        if (nextIndex !== null) setStepIndex(nextIndex);
      } catch (error) {
        // The `result?.failed` toast above only covers a PARTIAL failure the server reported —
        // an outright throw (network error, 500) reached here with no feedback at all otherwise.
        AlertsModel.errorToast(error instanceof Error ? error.message : "Couldn’t approve this policy, please try again.");
      } finally {
        setApplying(false);
      }
    },
    [orgId, sourceId, locationId, onFinish]
  );

  // Approve the given findings (minus denied ones), confirming first if any left carry "Won't apply".
  const requestApprove = useCallback(
    (toApprove: PolicyFieldCandidate[], finish: boolean, nextIndex: number | null, markKeys?: string[]): void => {
      const active = toApprove.filter((candidate) => !denied.has(candidate.mapKey));
      const mapKeys = active.map((candidate) => candidate.mapKey);
      if (active.some((candidate) => candidate.status === "needs-review")) setConfirming({ mapKeys, finish, nextIndex, markKeys });
      else void runApprove(mapKeys, finish, nextIndex, markKeys);
    },
    [runApprove, denied]
  );

  const continueApprove = (): void => {
    if (!confirming) return;
    const { mapKeys, finish, nextIndex, markKeys } = confirming;
    setConfirming(null);
    void runApprove(mapKeys, finish, nextIndex, markKeys);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-grey-500">
        <Spinner />
        <span className="body-sm">Loading proposed policies…</span>
      </div>
    );
  }

  // Distinct from "no policy changes" below — that reads as a genuine, reviewed "nothing to do";
  // this is a load that never actually happened, so it must never fall through to that message.
  if (loadError) {
    return (
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <p className="body-sm text-grey-500">Couldn’t load these policy fields.</p>
        <Button color={ButtonColor.ORANGE} onClick={() => void load()}>
          Try again
        </Button>
      </div>
    );
  }

  const finishNow = (): void => void runApprove([], true, null); // records sign-off (all reviewed)
  const closeForNow = (): void => (onClose ? onClose() : onFinish()); // leave policies pending, no sign-off

  if (sections.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <p className="body-sm text-grey-500">No policy changes to review, everything already matches.</p>
        <Button color={ButtonColor.ORANGE} loading={applying} onClick={finishNow}>
          {finishText}
        </Button>
        {onBack && (
          <Button variant={ButtonVariant.GHOST} disabled={applying} onClick={onBack}>
            Back
          </Button>
        )}
      </div>
    );
  }

  const total = orderedSections.length;
  const pendingSections = orderedSections.filter((reviewSection) => !approved.has(reviewSection.worksheet.policyKey));
  const reviewedCount = total - pendingSections.length;
  const allReviewed = pendingSections.length === 0;
  const safeIndex = Math.min(stepIndex, total - 1);
  const section = orderedSections[safeIndex];
  const isLast = safeIndex === total - 1;

  // Requeue the current policy behind the others; a no-op when it's the only one left.
  const handleDefer = (policyKey: string): void => {
    if (isLast) {
      AlertsModel.successToast("This is the last policy - approve it or deny fields you disagree with.");
      return;
    }
    setDeferred((prev) => [...prev.filter((key) => key !== policyKey), policyKey]);
  };

  const toggleDeny = (mapKey: string): void => {
    setDenied((prev) => {
      const next = new Set(prev);
      if (next.has(mapKey)) next.delete(mapKey);
      else next.add(mapKey);
      return next;
    });
  };

  const expand = (policyKey: string): void => setExpanded((prev) => new Set(prev).add(policyKey));

  // One policy's fields; `onApprove` renders the per-card button in the "all" view.
  const renderSection = (reviewSection: ReviewSection, opts?: { onApprove?: () => void; approved?: boolean }): React.ReactElement => (
    <div key={reviewSection.worksheet.policyKey}>
      <div className="mb-1 text-xs font-semibold tracking-wider text-grey-400 uppercase">{reviewSection.worksheet.groupLabel ?? "General"}</div>
      <div className="mb-3 flex items-center gap-2">
        <h3 className="font-poppins text-xl font-semibold text-grey-900">{reviewSection.worksheet.title}</h3>
        {opts?.approved && (
          <Badge className="flex items-center gap-1 bg-teal-100 text-teal-700">
            <FiCheck className="size-3.5" /> Approved
          </Badge>
        )}
      </div>
      <div className="divide-y divide-grey-100 rounded-2xl border border-grey-200 bg-white px-5 py-1 shadow-panel">
        {reviewSection.candidates.map((candidate) => (
          <CandidateRow
            key={candidate.mapKey}
            candidate={candidate}
            item={itemByMapKey.get(candidate.mapKey)}
            saving={savingKey === candidate.mapKey}
            onSave={(value) => handleSave(candidate.mapKey, value)}
            denied={denied.has(candidate.mapKey)}
            onToggleDeny={() => toggleDeny(candidate.mapKey)}
          />
        ))}
      </div>
      {opts?.onApprove && (
        <div className="mt-2 flex justify-end">
          <Button color={ButtonColor.ORANGE} disabled={applying} onClick={opts.onApprove}>
            {opts.approved ? "Re-approve" : "Approve"}
          </Button>
        </div>
      )}
    </div>
  );

  // Compact strip for an approved policy in the "all" view; "Edit" re-opens it.
  const renderCollapsed = (reviewSection: ReviewSection): React.ReactElement => (
    <div
      key={reviewSection.worksheet.policyKey}
      className="flex items-center justify-between gap-3 rounded-2xl border border-grey-200 bg-grey-50 px-5 py-3"
    >
      <div className="flex min-w-0 items-center gap-2">
        <FiCheck className="size-4 shrink-0 text-teal-600" />
        <span className="body-sm-alt truncate text-grey-700">{reviewSection.worksheet.title}</span>
        <Badge className="bg-teal-100 text-teal-700">Approved</Badge>
      </div>
      <Button variant={ButtonVariant.GHOST} disabled={applying} onClick={() => expand(reviewSection.worksheet.policyKey)}>
        Edit
      </Button>
    </div>
  );

  if (allReviewed && !reviewingAgain) {
    const summary = [`${appliedCount} applied`, ...(denied.size ? [`${denied.size} denied`] : [])].join(" · ");
    return (
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <div className="flex size-12 items-center justify-center rounded-full bg-teal-100 text-teal-700">
          <FiCheck className="size-6" />
        </div>
        <h3 className="font-poppins text-xl font-semibold text-grey-900">All policies reviewed</h3>
        <p className="body-sm text-grey-500">{summary}</p>
        <Button color={ButtonColor.ORANGE} loading={applying} onClick={finishNow}>
          {finishText}
        </Button>
        <div className="flex items-center gap-2">
          {onBack && (
            <Button variant={ButtonVariant.GHOST} disabled={applying} onClick={onBack}>
              Back
            </Button>
          )}
          <Button variant={ButtonVariant.GHOST} disabled={applying} onClick={() => setReviewingAgain(true)}>
            Back to review policies
          </Button>
        </div>
      </div>
    );
  }

  const footerFinish = allReviewed ? finishText : "Save & close";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-1.5 w-40 overflow-hidden rounded-full bg-grey-200">
            <div className="h-full rounded-full bg-orange-500 transition-all" style={{ width: `${Math.round((reviewedCount / total) * 100)}%` }} />
          </div>
          <span className="body-xs shrink-0 text-grey-500">
            {reviewedCount} of {total} reviewed
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="body-sm text-grey-600">Review all policies at once</span>
          <Switch
            variant={SwitchVariant.TOGGLE}
            activeColor="orange"
            on={showAll}
            isDisabled={applying}
            onSet={(next) => {
              setShowAll(next);
              // Switching to step-by-step lands on the first policy still awaiting a decision.
              if (!next) {
                const firstPending = orderedSections.findIndex((s) => !approved.has(s.worksheet.policyKey));
                setStepIndex(firstPending === -1 ? 0 : firstPending);
              }
            }}
          />
        </div>
      </div>

      {showAll ? (
        <div className="flex flex-col gap-6">
          {orderedSections.map((reviewSection) => {
            const isApproved = approved.has(reviewSection.worksheet.policyKey);
            if (isApproved && !expanded.has(reviewSection.worksheet.policyKey)) return renderCollapsed(reviewSection);
            return renderSection(reviewSection, {
              approved: isApproved,
              onApprove: () => requestApprove(reviewSection.candidates, false, null, [reviewSection.worksheet.policyKey])
            });
          })}
        </div>
      ) : (
        renderSection(section, { approved: approved.has(section.worksheet.policyKey) })
      )}

      {confirming ? (
        <div className="flex items-center justify-between gap-3 border-t border-grey-200 pt-3">
          <span className="body-sm text-red-600">Policies with the “Won’t apply” flag won’t be applied. Continue or fix?</span>
          <div className="flex shrink-0 items-center gap-2">
            <Button variant={ButtonVariant.GHOST} disabled={applying} onClick={() => setConfirming(null)}>
              Fix
            </Button>
            <Button color={ButtonColor.ORANGE} loading={applying} onClick={() => continueApprove()}>
              Continue
            </Button>
          </div>
        </div>
      ) : showAll ? (
        <div className="flex items-center justify-between border-t border-grey-200 pt-3">
          <Button variant={ButtonVariant.GHOST} loading={applying} onClick={allReviewed ? finishNow : closeForNow}>
            {footerFinish}
          </Button>
          {pendingSections.length > 0 && (
            <Button
              color={ButtonColor.ORANGE}
              loading={applying}
              onClick={() =>
                requestApprove(
                  pendingSections.flatMap((s) => s.candidates),
                  false,
                  null,
                  pendingSections.map((s) => s.worksheet.policyKey)
                )
              }
            >
              Approve all
            </Button>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-between border-t border-grey-200 pt-3">
          <Button variant={ButtonVariant.GHOST} loading={applying} onClick={allReviewed ? finishNow : closeForNow}>
            {footerFinish}
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant={ButtonVariant.GHOST}
              disabled={applying || (safeIndex === 0 && !onBack)}
              onClick={() => (safeIndex === 0 ? onBack?.() : setStepIndex(safeIndex - 1))}
            >
              Back
            </Button>
            {total > 1 && (
              <Button variant={ButtonVariant.GHOST} disabled={applying} onClick={() => handleDefer(section.worksheet.policyKey)}>
                Skip for now
              </Button>
            )}
            <Button
              color={ButtonColor.ORANGE}
              loading={applying}
              onClick={() => requestApprove(section.candidates, false, isLast ? null : safeIndex + 1, [section.worksheet.policyKey])}
            >
              {approved.has(section.worksheet.policyKey) ? "Re-approve" : "Approve"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
